import React from "react";
import {
  Stack,
  Button,
  useColorScheme,
  CircularProgress,
  Typography
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import { IoIosHelpCircleOutline } from "react-icons/io";
import {
  actionBarButtonPrimaryDarkStyle,
  actionBarButtonPrimaryLightStyle
} from "../styles";
import { PoweredByText } from "./Branding/PoweredBy";
import { useScreenSize } from "../hooks/useScreenSize";
import { useTranslation } from "react-i18next";
import ErrorListTooltip from "./Tooltip/ErrorListTooltip";
import { focusRingStyles } from "../utils/accessibility";
interface ActionBarProps {
  onReset: () => void;
  onBack?: () => void;
  onSubmit: () => void;
  showHelp?: () => void;
  showBackButton?: boolean;
  isSubmitDisabled: boolean;
  isResetDisabled: boolean;
  isHandlingBack?: boolean;
  showPoweredBy?: boolean;
  middleContent?: React.ReactNode;
  errors?: number[];
  warnings?: number[];
}

export const ActionBar: React.FC<ActionBarProps> = ({
  errors = [],
  warnings = [],
  onReset,
  onBack,
  onSubmit,
  showHelp,
  showBackButton = false,
  isResetDisabled,
  isSubmitDisabled,
  isHandlingBack = false,
  showPoweredBy = true,
  middleContent
}) => {
  const { mode: themeVariant } = useColorScheme();
  const { isSmallScreen, isLargeScreen } = useScreenSize();
  const { t } = useTranslation();

  return (
    <Stack
      py={isLargeScreen ? 1.5 : 0}
      px={1.5}
      alignItems="center"
      justifyContent="space-between"
      direction="row"
      sx={{ position: "relative" }}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="center"
      >
        <Button
          variant="text"
          size="large"
          onClick={onReset}
          disabled={isResetDisabled}
          aria-label={t("ROOSTER.PUBLIC-APP.RESET", { defaultValue: "Reset" })}
          sx={{
            ...(themeVariant === "light"
              ? {
                  ...actionBarButtonPrimaryLightStyle("text"),
                  color: "#375990"
                }
              : actionBarButtonPrimaryDarkStyle("text")),
            backgroundColor: "transparent",
            border: "0 !important",
            minWidth: isSmallScreen ? "auto" : "64px",
            "&:hover": {
              border: isSmallScreen ? 0 : null
            },
            ...focusRingStyles
          }}
        >
          <RestartAltIcon />
          {!isSmallScreen ? (
            <>
              &nbsp;
              <Typography>
                {t("ROOSTER.PUBLIC-APP.RESET", { defaultValue: "Reset" })}
              </Typography>
            </>
          ) : null}
        </Button>
        {showHelp && (
          <Button
            variant="text"
            size="large"
            onClick={showHelp}
            aria-label={t("ROOSTER.PUBLIC-APP.HELP", { defaultValue: "Help" })}
            sx={{
              ...(themeVariant === "light"
                ? {
                    ...actionBarButtonPrimaryLightStyle("text"),
                    color: "#375990"
                  }
                : actionBarButtonPrimaryDarkStyle("text")),
              backgroundColor: "transparent",
              border: "0 !important",
              marginLeft: "0 !important",
              ...focusRingStyles
            }}
          >
            <IoIosHelpCircleOutline size={"26px"} />
            {!isSmallScreen ? (
              <>
                &nbsp;
                <Typography>
                  {t("ROOSTER.PUBLIC-APP.HELP", { defaultValue: "Help" })}
                </Typography>
              </>
            ) : null}
          </Button>
        )}
      </Stack>

      {/* Middle content - floating trigger */}
      {middleContent}

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="center"
      >
        {errors.length > 0 ? (
          <ErrorListTooltip errorCodes={errors} />
        ) : (
          warnings.length > 0 && (
            <ErrorListTooltip type="warning" errorCodes={warnings} />
          )
        )}
        {showPoweredBy && (
          <Stack
            sx={{
              border: `1px solid ${themeVariant === "dark" ? "#2f2f2f" : "#e0e0e0"}`,
              borderRadius: "5px",
              paddingX: "8px",
              paddingY: "3px"
            }}
          >
            <PoweredByText show />
          </Stack>
        )}
        {showBackButton && (
          <Button
            variant="text"
            size="large"
            onClick={onBack}
            disabled={isHandlingBack}
            aria-label={t("ROOSTER.PUBLIC-APP.BACK", { defaultValue: "Back" })}
            aria-busy={isHandlingBack}
            sx={{
              ...(themeVariant === "light"
                ? {
                    ...actionBarButtonPrimaryLightStyle("text"),
                    color: "#375990"
                  }
                : actionBarButtonPrimaryDarkStyle("text")),
              backgroundColor: "transparent",
              minWidth: isSmallScreen ? "auto" : "64px",
              border: "0 !important",
              ...focusRingStyles
            }}
          >
            {isHandlingBack ? (
              <>
                <CircularProgress
                  size={18}
                  color="inherit"
                  aria-label={t("ROOSTER.PUBLIC-APP.LOADING", {
                    defaultValue: "Loading"
                  })}
                />{" "}
                &nbsp;&nbsp;{" "}
                {!isSmallScreen && (
                  <Typography>
                    {t("ROOSTER.PUBLIC-APP.BACK", { defaultValue: "Back" })}
                  </Typography>
                )}
              </>
            ) : (
              <>
                <ArrowBackIcon /> &nbsp;{" "}
                {!isSmallScreen && (
                  <Typography>
                    {t("ROOSTER.PUBLIC-APP.BACK", { defaultValue: "Back" })}
                  </Typography>
                )}
              </>
            )}
          </Button>
        )}
        <Button
          variant="outlined"
          size="large"
          onClick={onSubmit}
          disabled={!isSubmitDisabled}
          aria-label={t("ROOSTER.PUBLIC-APP.SUBMIT", {
            defaultValue: "Submit"
          })}
          sx={{
            ...(themeVariant === "light"
              ? actionBarButtonPrimaryLightStyle("contained")
              : actionBarButtonPrimaryDarkStyle("contained")),
            ...focusRingStyles
          }}
        >
          {!isSmallScreen ? (
            <>
              <CheckIcon /> &nbsp;
            </>
          ) : null}{" "}
          <Typography>
            {t("ROOSTER.PUBLIC-APP.SUBMIT", { defaultValue: "Submit" })}
          </Typography>
        </Button>
      </Stack>
    </Stack>
  );
};
